export interface PasswordRequirement {
  key: string;
  label: string;
  test: (password: string) => boolean;
  satisfied?: boolean;
}

export interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  percentage: number;
}

// Helper function to get translated password requirements
export function getPasswordRequirements(t: (key: string) => string): PasswordRequirement[] {
  return [
    {
      key: 'length',
      label: t('passwordValidation.requirements.length'),
      test: (password: string) => password.length >= 8,
    },
    {
      key: 'lowercase',
      label: t('passwordValidation.requirements.lowercase'),
      test: (password: string) => /[a-z]/.test(password),
    },
    {
      key: 'uppercase',
      label: t('passwordValidation.requirements.uppercase'),
      test: (password: string) => /[A-Z]/.test(password),
    },
    {
      key: 'numbers',
      label: t('passwordValidation.requirements.numbers'),
      test: (password: string) => /[0-9]/.test(password),
    },
    {
      key: 'special',
      label: `${t('passwordValidation.requirements.special')} (!@#$%^&*)`,
      test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
    },
  ];
}

// Default requirements for backward compatibility
export const passwordRequirements: PasswordRequirement[] = [
  {
    key: 'length',
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    key: 'lowercase',
    label: 'Contains lowercase letters (a-z)',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    key: 'uppercase',
    label: 'Contains uppercase letters (A-Z)',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    key: 'numbers',
    label: 'Contains numbers (0-9)',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    key: 'special',
    label: 'Contains special characters (!@#$%^&*)',
    test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
  },
];

export function validatePassword(password: string, requirements?: PasswordRequirement[]): PasswordRequirement[] {
  const reqs = requirements || passwordRequirements;
  return reqs.map(requirement => ({
    ...requirement,
    satisfied: requirement.test(password),
  }));
}

export function calculatePasswordStrength(password: string, t?: (key: string) => string, requirements?: PasswordRequirement[]): PasswordStrength {
  if (!password) {
    const label = t ? t('passwordValidation.strength.veryWeak') : 'Very Weak';
    return { score: 0, label, color: 'bg-red-500', percentage: 0 };
  }

  const reqs = requirements || passwordRequirements;
  const satisfiedRequirements = validatePassword(password, reqs).filter(req => req.satisfied);
  const score = satisfiedRequirements.length;
  const percentage = Math.round((score / reqs.length) * 100);

  let label = 'Very Weak';
  let color = 'bg-red-500';

  if (t) {
    // Use translations if available
    if (score >= 5) {
      label = t('passwordValidation.strength.strong');
      color = 'bg-green-500';
    } else if (score >= 4) {
      label = t('passwordValidation.strength.good');
      color = 'bg-yellow-500';
    } else if (score >= 3) {
      label = t('passwordValidation.strength.fair');
      color = 'bg-orange-500';
    } else if (score >= 2) {
      label = t('passwordValidation.strength.weak');
      color = 'bg-red-400';
    } else {
      label = t('passwordValidation.strength.veryWeak');
      color = 'bg-red-500';
    }
  } else {
    // Fallback to English if no translation function
    if (score >= 5) {
      label = 'Strong';
      color = 'bg-green-500';
    } else if (score >= 4) {
      label = 'Good';
      color = 'bg-yellow-500';
    } else if (score >= 3) {
      label = 'Fair';
      color = 'bg-orange-500';
    } else if (score >= 2) {
      label = 'Weak';
      color = 'bg-red-400';
    }
  }

  return { score, label, color, percentage };
}

export function isPasswordValid(password: string, requirements?: PasswordRequirement[]): boolean {
  const reqs = requirements || passwordRequirements;
  return validatePassword(password, reqs).every(req => req.satisfied);
}

export function getPasswordValidationMessage(password: string, t?: (key: string) => string, requirements?: PasswordRequirement[]): string | null {
  if (!password) {
    return t ? t('passwordValidation.messages.required') : 'Password is required';
  }
  
  const reqs = requirements || passwordRequirements;
  const unsatisfiedRequirements = validatePassword(password, reqs).filter(req => !req.satisfied);
  
  if (unsatisfiedRequirements.length === 0) return null;
  
  const requirementsList = unsatisfiedRequirements.map(req => req.label.toLowerCase()).join(', ');
  
  if (t) {
    return t('passwordValidation.messages.mustMeetRequirements').replace('{requirements}', requirementsList);
  }
  
  return `Password must meet the following requirements: ${requirementsList}`;
}
